import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import QRCode from 'react-qr-code';
import { useTheme } from './useTheme';
import './WhatsAppStatusPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003';
const WS_URL = API_BASE_URL.replace(/^http/, 'ws');

const loadFontAwesome = () => {
    if (!document.getElementById('font-awesome-css')) {
        const link = document.createElement('link');
        link.id = 'font-awesome-css';
        link.rel = 'stylesheet';
        link.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css';
        document.head.appendChild(link);
    }
};

function WhatsAppStatusPage() {
    const navigate = useNavigate();
    const { theme, toggleTheme } = useTheme();
    const [status, setStatus] = useState('loading');
    const [qrCode, setQrCode] = useState(null);
    const [connectedAt, setConnectedAt] = useState(null);
    const [disconnectReason, setDisconnectReason] = useState(null);
    const [restarting, setRestarting] = useState(false);
    const [error, setError] = useState(null);
    const wsRef = useRef(null);

    useEffect(() => {
        loadFontAwesome();
        fetchStatus();
        connectWebSocket();
        return () => {
            if (wsRef.current) wsRef.current.close();
        };
    }, []);

    const fetchStatus = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/whatsapp-status`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Error fetching status');
            const data = await response.json();
            setStatus(data.status);
            setQrCode(data.qr);
            setConnectedAt(data.connectedAt);
            setDisconnectReason(data.disconnectReason);
        } catch (err) {
            setError('No se pudo obtener el estado de WhatsApp');
        }
    };

    const connectWebSocket = () => {
        const ws = new WebSocket(WS_URL);
        wsRef.current = ws;
        ws.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                if (data.type === 'qr') {
                    setStatus('qr_pending');
                    setQrCode(data.qr);
                    setRestarting(false);
                    setError(null);
                } else if (data.type === 'whatsapp-ready') {
                    setStatus('connected');
                    setQrCode(null);
                    setConnectedAt(new Date().toISOString());
                    setRestarting(false);
                    setError(null);
                } else if (data.type === 'whatsapp-disconnected') {
                    setStatus('disconnected');
                    setQrCode(null);
                    setDisconnectReason(data.reason);
                    setRestarting(false);
                } else if (data.type === 'whatsapp-restarting') {
                    setStatus('initializing');
                    setRestarting(true);
                }
            } catch (e) {
                // Ignorar mensajes no JSON
            }
        };
        ws.onclose = () => {
            console.log('WebSocket desconectado');
        };
    };

    const handleRestart = async () => {
        setRestarting(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/whatsapp-restart`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error);
            }
        } catch (err) {
            setError(err.message);
            setRestarting(false);
        }
    };

    const formatDate = (isoString) => {
        if (!isoString) return '';
        const date = new Date(isoString);
        return date.toLocaleString('es-MX', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusInfo = () => {
        switch (status) {
            case 'connected':
                return { label: 'Conectado', dotClass: 'connected', icon: 'fa-check-circle', color: '#00a884' };
            case 'qr_pending':
                return { label: 'Esperando escaneo QR', dotClass: 'pending', icon: 'fa-qrcode', color: '#fbbf24' };
            case 'initializing':
                return { label: 'Iniciando...', dotClass: 'pending', icon: 'fa-spinner fa-spin', color: '#fbbf24' };
            case 'disconnected':
                return { label: 'Desconectado', dotClass: 'disconnected', icon: 'fa-times-circle', color: '#ff6b6b' };
            case 'auth_failure':
                return { label: 'Error de autenticacion', dotClass: 'disconnected', icon: 'fa-exclamation-triangle', color: '#ff6b6b' };
            default:
                return { label: 'Cargando...', dotClass: 'pending', icon: 'fa-spinner fa-spin', color: '#999' };
        }
    };

    const statusInfo = getStatusInfo();

    return (
        <div className="status-container">
            <div className="status-header">
                <button onClick={() => navigate('/conversations')} className="back-btn">
                    <i className="fas fa-arrow-left"></i>
                    <span>Conversaciones</span>
                </button>
                <button onClick={toggleTheme} className="back-btn" title={theme === 'light' ? 'Modo Oscuro' : 'Modo Claro'}>
                    <i className={theme === 'light' ? 'fas fa-moon' : 'fas fa-sun'}></i>
                </button>
            </div>

            <div className="status-content">
                {/* Status */}
                <div className="status-section">
                    <div className="status-indicator">
                        <span className={`status-dot ${statusInfo.dotClass}`}></span>
                        <i className={`fas ${statusInfo.icon}`} style={{ color: statusInfo.color, fontSize: '18px' }}></i>
                        <span className="status-label" style={{ color: statusInfo.color }}>
                            {statusInfo.label}
                        </span>
                    </div>

                    {status === 'connected' && connectedAt && (
                        <p className="status-detail">
                            Conectado desde: {formatDate(connectedAt)}
                        </p>
                    )}

                    {(status === 'disconnected' || status === 'auth_failure') && disconnectReason && (
                        <p className="status-detail error-text">
                            Razon: {disconnectReason}
                        </p>
                    )}
                </div>

                {/* QR Code */}
                {status === 'qr_pending' && qrCode && (
                    <div className="qr-section">
                        <h3 className="qr-title">Escanea el codigo QR</h3>
                        <div className="qr-container">
                            <QRCode value={qrCode} size={220} />
                        </div>
                        <ol className="qr-instructions">
                            <li>Abre <strong>WhatsApp</strong> en tu telefono</li>
                            <li>Ve a <strong>Ajustes</strong> &rarr; <strong>Dispositivos vinculados</strong></li>
                            <li>Toca en <strong>Vincular un dispositivo</strong></li>
                            <li>Apunta tu telefono a esta pantalla</li>
                        </ol>
                        <p className="qr-note">El codigo se actualiza automaticamente si expira</p>
                    </div>
                )}

                {/* Error */}
                {error && (
                    <div className="error-section">
                        <i className="fas fa-exclamation-triangle"></i>
                        <span>{error}</span>
                    </div>
                )}

                {/* Action buttons */}
                <div className="status-actions">
                    {status !== 'connected' && (
                        <button
                            className="restart-btn"
                            onClick={handleRestart}
                            disabled={restarting}
                        >
                            <i className={restarting ? 'fas fa-spinner fa-spin' : 'fas fa-redo'}></i>
                            <span>{restarting ? 'Reiniciando...' : 'Reiniciar WhatsApp'}</span>
                        </button>
                    )}

                    {status === 'connected' && (
                        <button className="go-conversations-btn" onClick={() => navigate('/conversations')}>
                            <i className="fas fa-comments"></i>
                            <span>Ir a conversaciones</span>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

export default WhatsAppStatusPage;
